import type { LocationEntry, MeasurementEntry, Survey } from './survey.model';

export const SURVEY_STORAGE_VERSION = 2;
export const SURVEY_PAGED_VERSION = 3;
export const LOCATIONS_PAGE_SIZE = 300;
export const SURVEY_MEASUREMENTS_SUBCOLLECTION = 'measurements';
export const MAX_SURVEY_DOC_BYTES = 900 * 1024;
export const MAX_INLINE_DOC_BYTES = 1024 * 1024;

export type SurveyLayoutVersion = 1 | 2 | 3;

export interface SheetSummary {
  name: string;
  sheetType: MeasurementEntry['sheetType'];
  environment?: string;
  locationCount: number;
  locationPages?: number;
  /** Rows that carry their own environment, keyed by environment. Remaining rows inherit `environment`. */
  rowEnvironments: Record<string, number>;
}

export type SheetManifest = Record<string, SheetSummary>;

export type SurveyMetadata<T> = Omit<T, 'measurements'> & { version: typeof SURVEY_STORAGE_VERSION | typeof SURVEY_PAGED_VERSION; sheets: SheetManifest };

export interface LocationPageDoc {
  index: number;
  page: number;
  locations: Partial<LocationEntry>[];
}

export interface SplitSurvey<T> {
  metadata: SurveyMetadata<T>;
  sheets: Record<string, MeasurementEntry>;
  pages: Record<string, LocationPageDoc>;
}

type WithMeasurements = { measurements?: Record<string | number, MeasurementEntry> | null };

export function isSplitSurvey(doc: { version?: unknown } | null | undefined): boolean {
  return doc?.version === SURVEY_STORAGE_VERSION || doc?.version === SURVEY_PAGED_VERSION;
}

export function locationPageDocId(index: string | number, page: number): string {
  return `${index}_p${page}`;
}

export function getLocationPageCount(rowCount: number): number {
  return Math.max(1, Math.ceil(rowCount / LOCATIONS_PAGE_SIZE));
}

function describeVersion(version: unknown): string {
  if (typeof version === 'string' || typeof version === 'number' || typeof version === 'boolean') {
    return String(version);
  }
  return JSON.stringify(version) ?? 'unknown';
}

function compareKeys(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

/** A survey saved by a newer app than this one. Reading it as version 1 would show an empty survey. */
export class UnsupportedSurveyVersionError extends Error {
  constructor(readonly version: unknown) {
    super(`This survey was saved by a newer version of the app (layout version ${describeVersion(version)}). Update the app to open it.`);
    this.name = 'UnsupportedSurveyVersionError';
  }
}

export function assertSupportedVersion(doc: { version?: unknown } | null | undefined): void {
  const version = doc?.version;
  if (version !== undefined && version !== null && version !== 1 && version !== SURVEY_STORAGE_VERSION && version !== SURVEY_PAGED_VERSION) {
    throw new UnsupportedSurveyVersionError(version);
  }
}

export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.fromEntries(Object.entries(val as Record<string, unknown>).sort(([a], [b]) => compareKeys(a, b)));
    }
    return val;
  });
}

export function estimateDocBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

export function sheetsEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

export function summarizeSheet(sheet: MeasurementEntry): SheetSummary {
  const locations = sheet.locationData?.locations ?? [];
  const rowEnvironments: Record<string, number> = {};
  for (const location of locations) {
    const environment = location?.environment;
    if (environment) {
      rowEnvironments[environment] = (rowEnvironments[environment] ?? 0) + 1;
    }
  }
  return {
    name: sheet.name,
    sheetType: sheet.sheetType,
    ...(sheet.locationData?.environment !== undefined && { environment: sheet.locationData.environment }),
    locationCount: locations.length,
    ...(getLocationPageCount(locations.length) > 1 && { locationPages: getLocationPageCount(locations.length) }),
    rowEnvironments,
  };
}

export function splitSheetPages(sheet: MeasurementEntry): { main: MeasurementEntry; pages: LocationPageDoc[] } {
  const locations = sheet.locationData?.locations ?? [];
  const pageCount = getLocationPageCount(locations.length);
  if (pageCount === 1) {
    return { main: sheet, pages: [] };
  }
  const pages: LocationPageDoc[] = [];
  for (let page = 1; page < pageCount; page++) {
    pages.push({ index: sheet.index, page, locations: locations.slice(page * LOCATIONS_PAGE_SIZE, (page + 1) * LOCATIONS_PAGE_SIZE) });
  }
  const main = { ...sheet, locationData: { ...sheet.locationData, locations: locations.slice(0, LOCATIONS_PAGE_SIZE), pageCount } } as MeasurementEntry;
  return { main, pages };
}

export function splitSurvey<T extends WithMeasurements>(survey: T, currentVersion?: number): SplitSurvey<T> {
  const { measurements, ...rest } = survey;
  const sheets: Record<string, MeasurementEntry> = {};
  const pages: Record<string, LocationPageDoc> = {};
  const manifest: SheetManifest = {};
  let paged = currentVersion === SURVEY_PAGED_VERSION;

  for (const [index, sheet] of Object.entries(measurements ?? {})) {
    if (!sheet) {
      continue;
    }
    const split = splitSheetPages(sheet);
    sheets[index] = split.main;
    for (const page of split.pages) {
      pages[locationPageDocId(index, page.page)] = page;
      paged = true;
    }
    manifest[index] = summarizeSheet(sheet);
  }

  const version = paged ? SURVEY_PAGED_VERSION : SURVEY_STORAGE_VERSION;
  return { metadata: { ...rest, version, sheets: manifest } as SurveyMetadata<T>, sheets, pages };
}

/**
 * Rebuilds the document shape the app has always used. Version 1 docs are returned untouched.
 * Throws when a sheet listed in the manifest has no doc: loading fewer sheets would let the next
 * Save Online silently delete them online.
 */
export function joinSurvey<T extends { version?: unknown; sheets?: SheetManifest; measurements?: unknown }>(
  metadata: T,
  sheetDocs: Record<string, MeasurementEntry>,
  pageDocs: Record<string, Pick<LocationPageDoc, 'locations'>> = {},
): Omit<T, 'version' | 'sheets'> & { measurements: Record<number, MeasurementEntry> } {
  if (!isSplitSurvey(metadata)) {
    const { measurements, ...rest } = metadata;
    return { ...rest, measurements: (measurements ?? {}) as Record<number, MeasurementEntry> } as never;
  }

  const { version: _version, sheets: manifest, ...rest } = metadata;
  const missing = Object.keys(manifest ?? {}).filter((index) => !sheetDocs[index]);
  if (missing.length > 0) {
    throw new Error(`Survey is incomplete online: missing sheet(s) ${missing.join(', ')}`);
  }

  const measurements = Object.fromEntries(
    Object.keys(manifest ?? {}).map((index) => [index, joinSheetPages(index, sheetDocs[index], manifest?.[index]?.locationPages ?? 1, pageDocs)]),
  );
  return { ...rest, measurements } as never;
}

function joinSheetPages(index: string, sheet: MeasurementEntry, pageCount: number, pageDocs: Record<string, Pick<LocationPageDoc, 'locations'>>): MeasurementEntry {
  if (pageCount <= 1) {
    return sheet;
  }
  const locations = [...(sheet.locationData?.locations ?? [])];
  for (let page = 1; page < pageCount; page++) {
    const pageDoc = pageDocs[locationPageDocId(index, page)];
    if (!pageDoc) {
      throw new Error(`Survey is incomplete online: missing location page ${page} of sheet ${index}`);
    }
    locations.push(...(pageDoc.locations ?? []));
  }
  const { pageCount: _pageCount, ...locationData } = sheet.locationData ?? {};
  return { ...sheet, locationData: { ...locationData, locations } } as MeasurementEntry;
}

export class SurveyTooLargeError extends Error {
  constructor(
    readonly docPath: string,
    readonly bytes: number,
    readonly sheetName?: string,
    limit = MAX_SURVEY_DOC_BYTES,
  ) {
    const mb = (bytes / (1024 * 1024)).toFixed(2);
    super(sheetName ? `Sheet "${sheetName}" is ${mb} MB, over the ${limit / 1024} KB online limit` : `Survey data is ${mb} MB, over the ${limit / 1024} KB online limit`);
    this.name = 'SurveyTooLargeError';
  }
}

export function assertWithinLimit(split: SplitSurvey<unknown>, surveyPath: string): void {
  for (const [index, sheet] of Object.entries(split.sheets)) {
    const bytes = estimateDocBytes(sheet);
    if (bytes > MAX_SURVEY_DOC_BYTES) {
      throw new SurveyTooLargeError(`${surveyPath}/${SURVEY_MEASUREMENTS_SUBCOLLECTION}/${index}`, bytes, sheet.name || `#${index}`);
    }
  }
  for (const [docId, page] of Object.entries(split.pages)) {
    const bytes = estimateDocBytes(page);
    if (bytes > MAX_SURVEY_DOC_BYTES) {
      const name = split.sheets[page.index]?.name || `#${page.index}`;
      throw new SurveyTooLargeError(`${surveyPath}/${SURVEY_MEASUREMENTS_SUBCOLLECTION}/${docId}`, bytes, `${name}, page ${page.page + 1}`);
    }
  }
  const metadataBytes = estimateDocBytes(split.metadata);
  if (metadataBytes > MAX_SURVEY_DOC_BYTES) {
    throw new SurveyTooLargeError(surveyPath, metadataBytes);
  }
}

export interface SurveySummary {
  sheetNames: string[];
  sheetCount: number;
  locationCount: number;
  environmentCounts: Record<string, number>;
}

export function summarizeSurvey(
  item: Pick<Survey, 'measurements'> & { sheets?: SheetManifest },
  resolveEnvironment: (row: { environment?: string } | undefined, sheet: { environment?: string } | undefined) => string,
): SurveySummary {
  const summary: SurveySummary = { sheetNames: [], sheetCount: 0, locationCount: 0, environmentCounts: {} };
  const add = (environment: string, count: number): void => {
    if (count > 0) {
      summary.environmentCounts[environment] = (summary.environmentCounts[environment] ?? 0) + count;
    }
  };

  if (item.measurements) {
    for (const sheet of Object.values(item.measurements)) {
      if (!sheet) {
        continue;
      }
      summary.sheetCount++;
      summary.sheetNames.push(sheet.name);
      for (const location of sheet.locationData?.locations ?? []) {
        summary.locationCount++;
        add(resolveEnvironment(location, sheet.locationData), 1);
      }
    }
    return summary;
  }

  for (const sheet of Object.values(item.sheets ?? {})) {
    summary.sheetCount++;
    summary.sheetNames.push(sheet.name);
    summary.locationCount += sheet.locationCount;
    let explicit = 0;
    for (const [environment, count] of Object.entries(sheet.rowEnvironments ?? {})) {
      explicit += count;
      add(environment, count);
    }
    add(resolveEnvironment(undefined, { environment: sheet.environment }), sheet.locationCount - explicit);
  }
  return summary;
}
